import { cn } from '@workingclock/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Three distinct device "chrome" shells for the cross-platform showcase.
 * Each uses a different chrome treatment (browser dots, phone notch, desktop
 * titlebar) so the row reads as three different form factors rather than
 * three copies of the same card — flat surfaces, borders only, no shadows
 * at rest (DESIGN.md's Flat-By-Default Rule). Chrome dots are neutral
 * (border-toned), not the traffic-light red/yellow/green, to avoid adding
 * colors outside the token set.
 */

export function BrowserFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-md border border-border bg-bg', className)}>
      <div className="flex items-center gap-1.5 border-b border-border bg-surface px-3 py-2.5">
        <span className="h-2 w-2 rounded-full bg-border" />
        <span className="h-2 w-2 rounded-full bg-border" />
        <span className="h-2 w-2 rounded-full bg-border" />
        <span className="ml-2 h-4 flex-1 rounded-sm bg-border/50" />
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function PhoneFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-lg border-2 border-ink/80 bg-bg', className)}>
      <div className="flex justify-center border-b border-border py-2">
        <span className="h-1.5 w-12 rounded-full bg-border" />
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function DesktopFrame({ children, className }: { children: ReactNode; className?: string }) {
  const { t } = useTranslation('marketing');
  return (
    <div className={cn('overflow-hidden rounded-md border border-border bg-bg', className)}>
      <div className="flex items-center justify-center border-b border-border bg-surface px-3 py-2">
        <span className="text-label text-muted">{t('brand.name')}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/** A compact timer readout: mono tabular digits + a project label, reused across the three frames. */
export function MiniTimer({ project, time, active = false }: { project: string; time: string; active?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-label text-muted">{project}</p>
        <p className="mt-0.5 font-mono text-headline tabular-nums text-ink">{time}</p>
      </div>
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          active ? 'bg-gold' : 'border border-border bg-transparent',
        )}
        aria-hidden
      >
        <span className={cn('h-2.5 w-2.5 rounded-sm', active ? 'bg-ink' : 'bg-muted')} />
      </span>
    </div>
  );
}

/** A short list of time entries (mono durations), used in the "desktop" frame to vary its content from the others. */
export function MiniEntryList({ entries }: { entries: Array<{ label: string; duration: string }> }) {
  return (
    <ul className="space-y-2">
      {entries.map((entry) => (
        <li key={entry.label} className="flex items-center justify-between gap-3 text-body">
          <span className="text-ink">{entry.label}</span>
          <span className="font-mono tabular-nums text-muted">{entry.duration}</span>
        </li>
      ))}
    </ul>
  );
}
